/**
 * 资源配置工具
 * 支持配置 CDN URL，自动替换图片链接中的域名
 *
 * 环境变量配置：
 * - VUE_APP_ASSETS_CDN_URL: 主 CDN URL (留空则使用相对路径)
 * - VUE_APP_ASSETS_CDN_URL_ALT: 备用 CDN URL (留空则使用相对路径)
 */

// 判断环境变量是否已定义（区分 undefined 和空字符串）
const hasCustomCdnUrl = process.env.VUE_APP_ASSETS_CDN_URL !== undefined;
const hasCustomCdnUrlAlt = process.env.VUE_APP_ASSETS_CDN_URL_ALT !== undefined;

// CDN 域名配置（如果环境变量已定义则使用其值，否则使用默认值）
const CDN_DOMAINS = {
  moguocdn: hasCustomCdnUrl ? process.env.VUE_APP_ASSETS_CDN_URL : 'https://cdn.moguocdn.com',
  comettech: hasCustomCdnUrlAlt ? process.env.VUE_APP_ASSETS_CDN_URL_ALT : 'https://cdn.comettech.com',
};

// 原始 CDN 域名（用于替换）
const ORIGINAL_DOMAINS = {
  moguocdn: 'https://cdn.moguocdn.com',
  comettech: 'https://cdn.comettech.com',
};

/**
 * 替换 URL 中的 CDN 域名
 * @param {string} url - 原始 URL
 * @returns {string} 替换后的 URL
 */
export function replaceCdnDomain(url) {
  if (!url) return '';

  let result = url;

  // 替换 moguocdn 域名
  if (result.includes('cdn.moguocdn.com')) {
    result = result.replace(ORIGINAL_DOMAINS.moguocdn, CDN_DOMAINS.moguocdn);
  }

  // 替换 comettech 域名
  if (result.includes('cdn.comettech.com')) {
    result = result.replace(ORIGINAL_DOMAINS.comettech, CDN_DOMAINS.comettech);
  }

  return result;
}

/**
 * 获取 moguocdn CDN URL
 * @param {string} path - 资源路径 (如 /cdn/static/images/empty.png)
 * @param {string} [suffix] - URL 后缀参数 (如 ?imageView2/0/w/100)
 * @returns {string} 完整的 CDN URL
 */
export function getMoguoCdnUrl(path, suffix = '') {
  if (!path) return '';

  // 如果已经是完整 URL，进行域名替换
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return replaceCdnDomain(path) + suffix;
  }

  // 确保路径以 / 开头
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${CDN_DOMAINS.moguocdn}${normalizedPath}${suffix}`;
}

/**
 * 获取 comettech CDN URL
 * @param {string} path - 资源路径
 * @param {string} [suffix] - URL 后缀参数
 * @returns {string} 完整的 CDN URL
 */
export function getComettechCdnUrl(path, suffix = '') {
  if (!path) return '';

  // 如果已经是完整 URL，进行域名替换
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return replaceCdnDomain(path) + suffix;
  }

  // 确保路径以 / 开头
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${CDN_DOMAINS.comettech}${normalizedPath}${suffix}`;
}

/**
 * 通用获取 CDN URL（自动识别域名）
 * @param {string} url - 原始 URL 或路径
 * @param {string} [suffix] - URL 后缀参数
 * @param {string} [defaultCdn] - 默认 CDN ('moguocdn' | 'comettech')
 * @returns {string} 完整的 CDN URL
 */
export function getCdnUrl(url, suffix = '', defaultCdn = 'moguocdn') {
  if (!url) return '';

  // 如果已经是完整 URL，进行域名替换
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return replaceCdnDomain(url) + suffix;
  }

  // 使用默认 CDN
  const cdnBase = CDN_DOMAINS[defaultCdn] || CDN_DOMAINS.moguocdn;
  const normalizedPath = url.startsWith('/') ? url : `/${url}`;
  return `${cdnBase}${normalizedPath}${suffix}`;
}

/**
 * 获取当前 CDN 配置
 * @returns {object} CDN 配置信息
 */
export function getCdnConfig() {
  return {
    moguocdn: CDN_DOMAINS.moguocdn,
    comettech: CDN_DOMAINS.comettech,
  };
}

export default {
  replaceCdnDomain,
  getMoguoCdnUrl,
  getComettechCdnUrl,
  getCdnUrl,
  getCdnConfig,
};
